/**
 * Push-notification backend error codes.
 *
 * Must match `ErrorCodes` in be/utils/errors.js (Push Notification group).
 *
 * Codes are the stable API contract; the backend's `error` string is Arabic
 * and exists for legacy clients and logs (I18N_STRATEGY.md — "API responses
 * are NOT localized"). Translation lives on the client, so a code maps to
 * whatever language the reader uses.
 *
 * Client rule: if a response carries `code`, translate it; otherwise fall back
 * to displaying the server's message. Never infer meaning from message text.
 *
 * ⚠️ These codes are translated from `web/src/locales/{ar,en}.js` (`notifications.errors.*`), via
 * the mapping in `config/notificationLabels.js`. This territory WAS Arabic-only, on the assumption
 * that the panel was core-staff and therefore unreachable by an English-locale user; the
 * `guest_checkout` notification type ended that in 2026-08 by addressing the cleaning crew. The
 * identifiers below did not change — only where their words live.
 */
export const NOTIFICATION_ERROR_CODES = {
  // The PUSH_NOTIFICATIONS_ENABLED flag is off for this environment.
  DISABLED: 'PUSH_001',
  // Flag on, but the server has no usable VAPID key pair — nothing can be sent
  // and nothing new can be registered. An operator-visible server problem.
  NOT_CONFIGURED: 'PUSH_002',
  // Endpoint host is not an allow-listed push service, or the keys are malformed.
  INVALID_SUBSCRIPTION: 'PUSH_003',
  // No such device, or not this user's — deliberately one code, so device ids
  // cannot be probed.
  DEVICE_NOT_FOUND: 'PUSH_004',
  // No such notification type in the backend registry.
  UNKNOWN_TYPE: 'PUSH_005',
  // A test send with no device registered yet: fix by subscribing this browser.
  NO_DEVICES: 'PUSH_006',
  // Devices exist and every push service rejected — a transport failure, not
  // something the operator did wrong. Distinct from NO_DEVICES on purpose.
  SEND_FAILED: 'PUSH_007'
};

export default NOTIFICATION_ERROR_CODES;
